import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import sum from "lodash/sum";
import Tooltip from "rc-tooltip";
import React from "react";
import { list as logistsList } from "sources/books/employees";
import CommonReport from "./commonReport";

export default class PlanFactReport extends CommonReport {
    formatData = (data) => {
        const result = [];
        data.forEach((row) => {
            const { carNumber, carLogistId, carLogistDisplayName } = row;
            if (row.bids.length > 0) {
                row.bids.forEach((bidRow) => {
                    result.push({ ...bidRow, carNumber, carLogistId, carLogistDisplayName, sum: row });
                });
            } else {
                result.push({ carNumber, carLogistId, carLogistDisplayName, sum: row, isEmpty: true });
            }
        });
        return result;
    };

    aggregatedValue = (subRows, level, columnName) =>
        level === 0
            ? sum(subRows.map((item) => item.leafRows[0].original.sum[columnName]))
            : subRows[0].original.sum[columnName];

    columnClrType = (propertyName) => {
        const { columns } = this.props.data || {};
        const column = columns.find((item) => columnName(item) === propertyName);
        return column ? column.clrType : null;
    };

    extractColumns = () => {
        return [
            {
                Header: "",
                id: "main",
                filterable: false,
                Footer: () => "",
                columns: [
                    {
                        Header: "Логист",
                        accessor: "carLogistId",
                        Cell: (props) => {
                            const { subRows } = props.row;
                            return subRows[0].subRows[0].original.carLogistDisplayName || "Без логиста";
                        },
                        Filter: reportFilterWrapper((props) => (
                            <SelectColumnFilter
                                {...props}
                                selectProps={{
                                    ...defaultMultipleSelectProps,
                                    url: logistsList,
                                    inputProps: { name: "carLogistIds" },
                                    propName: "fullNameOrEmail",
                                    components: { ValueContainer },
                                }}
                            />
                        )),
                        filter: (rows, _id, filterValue) => {
                            if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                                return rows;
                            }
                            return rows.filter((row) => filterValue.value.includes(row.values.carLogistId));
                        },
                    },
                    {
                        ...typeSpecificProps({}),
                        Header: "Машина",
                        accessor: "carNumber",
                        Aggregated: () => "",
                        Cell: (props) => {
                            const { subRows } = props.row;
                            return (
                                <span data-car={subRows[0].original.carNumber}>
                                    {props.value} {subRows && !subRows[0].original.isEmpty && `(${subRows.length})`}
                                </span>
                            );
                        },
                    },
                ],
            },
            {
                Header: "Заявка",
                accessor: "bidId",
                ...typeSpecificProps({}),
                Cell: ({ value, row: { original } }) => (
                    <>
                        <a
                            data-bidid={value}
                            href={paths.bidShow("bid", value)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {value}
                        </a>
                        {original.hasManualMileage && (
                            <Tooltip overlay="Закрыта вручную">
                                <span
                                    className="pl-1 icon-uEA83-user-edit b-point__tooltip-icon"
                                    data-for={`manualMileage${value}`}
                                />
                            </Tooltip>
                        )}
                        {original.hasCarChange && (
                            <Tooltip overlay="Произошла смена машины">
                                <span
                                    className="pl-1 icon-uEAB3-arrows-rotate b-point__tooltip-icon"
                                    data-for={`carChange${value}`}
                                />
                            </Tooltip>
                        )}
                        {original.hasEmulatedMileage && (
                            <Tooltip overlay="В заявке присутствуют сэмулированные участки маршрута">
                                <i className="pl-1 icon-uEACB-emulated-route b-point__tooltip-icon" />
                            </Tooltip>
                        )}
                    </>
                ),
                Aggregated: () => "",
            },
            {
                Header: "Внешний ID Заявки",
                accessor: "externalBidId",
                ...typeSpecificProps({}),
                Cell: ({ value }) => value,
                Aggregated: () => "",
            },
            {
                Header: "Активный пробег, км",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Первичный план",
                        accessor: "initialPlanMileage",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "InitialPlanMileage",
                                aggregateType: true,
                                clrType: this.columnClrType("initialPlanMileage"),
                            }),
                        Aggregated: ({ row: { subRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(subRows, depth, "initialPlanMileage"),
                                    this.columnClrType("initialPlanMileage"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-initialactivemileageplan={values.bidId}>
                                {formatValue(value, this.columnClrType("initialPlanMileage"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "План",
                        accessor: "activeMileagePlan",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "ActiveMileagePlan",
                                aggregateType: true,
                                clrType: this.columnClrType("activeMileagePlan"),
                            }),
                        Aggregated: ({ row: { subRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(subRows, depth, "activeMileagePlan"),
                                    this.columnClrType("activeMileagePlan"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values, original } }) => {
                            const cellValue = this.props.byEmptyMileage ? original.planMileage : value;
                            return (
                                <span data-activemileageplan={values.bidId}>
                                    {formatValue(cellValue, this.columnClrType("activeMileagePlan"))}
                                </span>
                            );
                        },
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Факт",
                        accessor: "activeMileageFact",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "ActiveMileageFact",
                                aggregateType: true,
                                clrType: this.columnClrType("activeMileageFact"),
                            }),
                        Aggregated: ({ row: { subRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(subRows, depth, "activeMileageFact"),
                                    this.columnClrType("activeMileageFact"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values, original } }) => {
                            const cellValue = this.props.byEmptyMileage ? original.factMileage : value;
                            return (
                                <span data-activemileagefact={values.bidId}>
                                    {formatValue(cellValue, this.columnClrType("activeMileageFact"))}
                                </span>
                            );
                        },
                    },
                ],
            },
            {
                Header: "Порожний пробег, км",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "План",
                        accessor: "emptyMileagePlan",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "EmptyMileagePlan",
                                aggregateType: true,
                                clrType: this.columnClrType("emptyMileagePlan"),
                            }),
                        Aggregated: ({ row: { subRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(subRows, depth, "emptyMileagePlan"),
                                    this.columnClrType("emptyMileagePlan"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-emptymileageplan={values.bidId}>
                                {formatValue(value, this.columnClrType("emptyMileagePlan"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Факт",
                        accessor: "emptyMileageFact",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "EmptyMileageFact",
                                aggregateType: true,
                                clrType: this.columnClrType("emptyMileageFact"),
                            }),
                        Aggregated: ({ row: { subRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(subRows, depth, "emptyMileageFact"),
                                    this.columnClrType("emptyMileageFact"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-emptymileagefact={values.bidId}>
                                {formatValue(value, this.columnClrType("emptyMileageFact"))}
                            </span>
                        ),
                    },
                ],
            },
            {
                ...typeSpecificProps({ clrType: "double" }),
                Header: "ВАЛ",
                accessor: "overallBidsPrice",
                Aggregated: ({ row: { subRows, depth } }) => (
                    <strong>
                        {hiddenUserData(
                            formatValue(
                                this.aggregatedValue(subRows, depth, "overallBidsPrice"),
                                this.columnClrType("overallBidsPrice"),
                            ),
                            { type: "price" },
                        )}
                    </strong>
                ),
                Cell: ({ row: { original } }) => {
                    const bidPrice = original?.bidPrice || 0;
                    return (
                        <span data-overallbidsprice={original.bidId}>
                            {hiddenUserData(formatValue(bidPrice, this.columnClrType("overallBidsPrice")), {
                                type: "price",
                            })}
                        </span>
                    );
                },
                Footer: () =>
                    hiddenUserData(
                        this.footerVal({
                            propertyName: "OverallBidsPrice",
                            aggregateType: true,
                            clrType: this.columnClrType("overallBidsPrice"),
                        }),
                        { type: "price" },
                    ),
            },
            {
                Header: `Активная выработка, ${this.props.organization?.currencyData?.name}/км`,
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Первичный план",
                        accessor: "outputInitialPlan",
                        Footer: () =>
                            hiddenUserData(
                                this.footerVal({
                                    propertyName: "OutputInitialPlan",
                                    aggregateType: true,
                                    clrType: this.columnClrType("outputInitialPlan"),
                                }),
                                { type: "pricePerVal" },
                            ),
                        Aggregated: ({ row: { subRows, depth } }) => {
                            if (depth === 0) {
                                const bidPriceSum = this.aggregatedValue(subRows, depth, "overallBidsPrice");
                                const initialPlanMileageSum = this.aggregatedValue(
                                    subRows,
                                    depth,
                                    "initialPlanMileage",
                                );
                                const output = initialPlanMileageSum === 0 ? 0 : bidPriceSum / initialPlanMileageSum;
                                return (
                                    <strong>
                                        {hiddenUserData(formatValue(output, this.columnClrType("outputInitialPlan")), {
                                            type: "pricePerVal",
                                        })}
                                    </strong>
                                );
                            } else {
                                return (
                                    <strong>
                                        {hiddenUserData(
                                            formatValue(
                                                this.aggregatedValue(subRows, depth, "outputInitialPlan"),
                                                this.columnClrType("outputInitialPlan"),
                                            ),
                                            { type: "pricePerVal" },
                                        )}
                                    </strong>
                                );
                            }
                        },
                        Cell: ({ value, row: { values } }) => (
                            <span data-initialoutputplan={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("outputInitialPlan")), {
                                    type: "pricePerVal",
                                })}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "План",
                        accessor: "outputPlan",
                        Footer: () =>
                            hiddenUserData(
                                this.footerVal({
                                    propertyName: "OutputPlan",
                                    aggregateType: true,
                                    clrType: this.columnClrType("outputPlan"),
                                }),
                                { type: "pricePerVal" },
                            ),
                        Aggregated: ({ row: { subRows, depth } }) => {
                            if (depth === 0) {
                                const bidPriceSum = this.aggregatedValue(subRows, depth, "overallBidsPrice");
                                const activeMileagePlanSum = this.aggregatedValue(subRows, depth, "activeMileagePlan");
                                const emptyMileagePlanSum = this.aggregatedValue(subRows, depth, "emptyMileagePlan");
                                const planMileageSum = activeMileagePlanSum + emptyMileagePlanSum;
                                const output = planMileageSum === 0 ? 0 : bidPriceSum / planMileageSum;
                                return (
                                    <strong>
                                        {hiddenUserData(formatValue(output, this.columnClrType("outputPlan")), {
                                            type: "pricePerVal",
                                        })}
                                    </strong>
                                );
                            } else {
                                return (
                                    <strong>
                                        {hiddenUserData(
                                            formatValue(
                                                this.aggregatedValue(subRows, depth, "outputPlan"),
                                                this.columnClrType("outputPlan"),
                                            ),
                                            { type: "pricePerVal" },
                                        )}
                                    </strong>
                                );
                            }
                        },
                        Cell: ({ value, row: { values } }) => (
                            <span data-outputplan={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("outputPlan")), {
                                    type: "pricePerVal",
                                })}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Факт",
                        accessor: "outputFact",
                        Footer: () =>
                            hiddenUserData(
                                this.footerVal({
                                    propertyName: "OutputFact",
                                    aggregateType: true,
                                    clrType: this.columnClrType("outputFact"),
                                }),
                                { type: "pricePerVal" },
                            ),
                        Aggregated: ({ row: { subRows, depth } }) => {
                            if (depth === 0) {
                                const bidPriceSum = this.aggregatedValue(subRows, depth, "overallBidsPrice");
                                const activeMileageFactSum = this.aggregatedValue(subRows, depth, "activeMileageFact");
                                const emptyMileageFactSum = this.aggregatedValue(subRows, depth, "emptyMileageFact");
                                const factMileageSum = activeMileageFactSum + emptyMileageFactSum;
                                const output = factMileageSum === 0 ? 0 : bidPriceSum / factMileageSum;
                                return (
                                    <strong>
                                        {hiddenUserData(formatValue(output, this.columnClrType("outputFact")), {
                                            type: "pricePerVal",
                                        })}
                                    </strong>
                                );
                            } else {
                                return (
                                    <strong>
                                        {hiddenUserData(
                                            formatValue(
                                                this.aggregatedValue(subRows, depth, "outputFact"),
                                                this.columnClrType("outputPlan"),
                                            ),
                                            { type: "pricePerVal" },
                                        )}
                                    </strong>
                                );
                            }
                        },
                        Cell: ({ value, row: { values } }) => (
                            <span data-outputfact={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("outputFact")), {
                                    type: "pricePerVal",
                                })}
                            </span>
                        ),
                    },
                ],
            },
        ];
    };

    tableExtraProps = () => {
        const { showInitialPlanValues, byEmptyMileage } = this.props;
        const hiddenColumns = [];
        if (!showInitialPlanValues) {
            hiddenColumns.push("initialPlanMileage", "outputInitialPlan");
        }
        if (byEmptyMileage) {
            hiddenColumns.push(
                "initialPlanMileage",
                "outputInitialPlan",
                "outputPlan",
                "outputFact",
                "overallBidsPrice",
            );
        }
        return {
            initialState: {
                groupBy: ["carLogistId", "carNumber"],
                hiddenColumns,
            },
            data: this.formatData(this.props.data.data),
        };
    };
}
